import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  EmptyState, BulkActionBar, BulkCheckbox,
  FilterDrawer, FilterSection, FilterChipGroup, ActiveFilters, FilterDateRange,
  SkeletonList,
} from "../../components/ui";
import { Icon } from "../shared/components";
import { ensureArray, ensureObject, formatTime } from "../shared/utils";

const STATUS_LABELS = {
  aberto: "Aberto",
  em_andamento: "Em andamento",
  aguardando: "Aguardando",
  resolvido: "Resolvido",
  fechado: "Fechado",
};

const PRIORITY_LABELS = {
  baixa: "Baixa",
  normal: "Normal",
  alta: "Alta",
  urgente: "Urgente",
  critica: "Crítica", // legado
};

const PRIORITY_TONES = {
  baixa: "green",
  normal: "blue",
  alta: "amber",
  urgente: "red",
  critica: "red",
};

function PriorityPill({ priority }) {
  return (
    <span className={`reportPill ${PRIORITY_TONES[priority] || "blue"}`}>
      {PRIORITY_LABELS[priority] || priority}
    </span>
  );
}

function StatusPillLocal({ status }) {
  const tones = { aberto: "blue", em_andamento: "amber", aguardando: "purple", resolvido: "green", fechado: "green" };
  return (
    <span className={`reportPill ${tones[status] || "blue"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ── Create / Edit modal ───────────────────────────────────────────────────────
const TICKET_CATEGORIES = ["Bug", "Dúvida", "Integração", "Cobrança", "Suporte técnico", "Melhoria", "Outro"];
const TAG_SUGGESTIONS   = ["urgente", "bug", "integracao", "cobranca", "duvida"];

const GHOST_TICKETS = [
  { id: 1, pillColor: "#ef4444" },
  { id: 2, pillColor: "#f59e0b" },
  { id: 3, pillColor: "#3b82f6" },
];

function TicketForm({ ticket, users, onSave, onClose, saving }) {
  const isNew = !ticket?.id;
  const [form, setForm] = useState({
    title:       ticket?.title       || "",
    description: ticket?.description || "",
    priority:    ticket?.priority    || "normal",
    assigned_to: ticket?.assigned_to || "",
    status:      ticket?.status      || "aberto",
    category:    ticket?.category    || "",
    contact_name: ticket?.contact_name || "",
    tags:        ensureArray(ticket?.tags),
  });
  const [tagInput, setTagInput] = useState("");

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function addTag(tag) {
    const t = tag.trim().toLowerCase().replace(/\s+/g, "_");
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  }
  function removeTag(tag) { set("tags", form.tags.filter((t) => t !== tag)); }
  function handleTagKey(e) {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
    if (e.key === "Backspace" && !tagInput && form.tags.length) set("tags", form.tags.slice(0, -1));
  }

  const PRIO_OPTIONS = [
    { value: "baixa",   label: "Baixa",   color: "#6b7280" },
    { value: "normal",  label: "Normal",  color: "#3b82f6" },
    { value: "alta",    label: "Alta",    color: "#f59e0b" },
    { value: "urgente", label: "Urgente", color: "#ef4444" },
  ];

  return (
    <div className="finModal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="finModal-box" style={{ maxWidth: 560 }}>

        {/* Header */}
        <div className="finModal-header">
          <div>
            <div className="finModal-title">{isNew ? "Novo ticket" : "Editar ticket"}</div>
            <div className="finModal-subtitle">Crie uma demanda rastreável de suporte ou atendimento</div>
          </div>
          <button type="button" className="finModal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        {/* Body */}
        <div className="finModal-body" style={{ gap: 16 }}>

          {/* Título */}
          <div className="finModal-section">
            <div className="finModal-fieldLabel">Título <span className="finModal-req">*</span></div>
            <input className="finModal-input" value={form.title} autoFocus
              onChange={(e) => set("title", e.target.value)}
              placeholder="Ex: Cliente não recebe SMS de confirmação" />
            <div className="finModal-hint">Resumo curto e claro da solicitação</div>
          </div>

          {/* Descrição */}
          <div className="finModal-section">
            <div className="finModal-fieldLabel">Descrição</div>
            <textarea className="finModal-textarea" value={form.description} rows={4}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Descreva o problema com o máximo de detalhes possível..." />
            <div className="finModal-hint">Detalhe o problema, passos para reproduzir, prints etc.</div>
          </div>

          {/* Prioridade + Categoria */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div className="finModal-section">
              <div className="finModal-fieldLabel">Prioridade</div>
              <div className="tkPriorityGrid">
                {PRIO_OPTIONS.map((p) => (
                  <button type="button" key={p.value}
                    className={`tkPriorityBtn${form.priority === p.value ? " active" : ""}`}
                    style={{ "--prio-color": p.color }}
                    onClick={() => set("priority", p.value)}
                  >
                    <span className="tkPriorityDot" />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="finModal-section">
              <div className="finModal-fieldLabel">Categoria</div>
              <select className="finModal-select" value={form.category} onChange={(e) => set("category", e.target.value)}>
                <option value="">Selecione...</option>
                {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Cliente + Responsável */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div className="finModal-section">
              <div className="finModal-fieldLabel">Cliente</div>
              <div className="tkClientSearch">
                <Icon name="search" size={13} style={{ color: "var(--text-4)", flexShrink: 0 }} />
                <input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Buscar contato..." />
              </div>
            </div>
            <div className="finModal-section">
              <div className="finModal-fieldLabel">Responsável</div>
              <select className="finModal-select" value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)}>
                <option value="">Sem responsável (fila)</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div className="finModal-section">
            <div className="finModal-fieldLabel">Tags</div>
            <div className="finModal-tagInput">
              {form.tags.map((tag) => (
                <span key={tag} className="finModal-tagChip">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>×</button>
                </span>
              ))}
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKey} placeholder={form.tags.length === 0 ? "Adicionar..." : ""} />
            </div>
            <div className="finModal-quickTags" style={{ marginTop: 8 }}>
              {TAG_SUGGESTIONS.filter((t) => !form.tags.includes(t)).map((t) => (
                <button type="button" key={t} className="finModal-quickTag" onClick={() => addTag(t)}>+ {t}</button>
              ))}
            </div>
          </div>

          {/* Status — apenas edição */}
          {!isNew && (
            <div className="finModal-section">
              <div className="finModal-fieldLabel">Status</div>
              <select className="finModal-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="finModal-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
          <div style={{ flex: 1 }} />
          {isNew && (
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => onSave({ ...form, status: "rascunho" })}
              disabled={saving || !form.title.trim()}>
              Salvar como rascunho
            </button>
          )}
          <button type="button" className="btn btn-primary btn-sm"
            onClick={() => onSave(form)} disabled={saving || !form.title.trim()}>
            <Icon name="ticket" size={13} /> {saving ? "Salvando…" : isNew ? "Criar ticket" : "Salvar"}
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Ticket detail panel ───────────────────────────────────────────────────────
function TicketDetail({ ticket, users, api, onClose, onUpdate }) {
  const [messages, setMessages] = useState(ensureArray(ticket.messages));
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);

  async function sendMsg(e) {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/tickets/${ticket.id}/messages`, { body: newMsg });
      setMessages((m) => [...m, res.data]);
      setNewMsg("");
      onUpdate();
    } catch {}
    setSending(false);
  }

  async function changeStatus(status) {
    try {
      await api.patch(`/tickets/${ticket.id}/status`, { status });
      onUpdate();
    } catch {}
  }

  return (
    <div className="ticketDetailPanel">
      <div className="ticketDetailHeader">
        <div>
          <h3>{ticket.title}</h3>
          <div className="ticketDetailMeta">
            <PriorityPill priority={ticket.priority} />
            <StatusPillLocal status={ticket.status} />
            {ticket.attendant_name && <span className="ticketAssignee">Responsável: {ticket.attendant_name}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={ticket.status}
            onChange={(e) => changeStatus(e.target.value)}
            className="ticketStatusSelect"
          >
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button className="ghostButton" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
      </div>

      {ticket.description && (
        <div className="ticketDescription">{ticket.description}</div>
      )}

      <div className="ticketTimeline">
        {messages.length === 0 && (
          <EmptyState illustration="chat" title="Nenhuma mensagem ainda" text="Adicione uma atualização abaixo." />
        )}
        {messages.map((m) => (
          <div key={m.id} className="ticketMessage">
            <div className="ticketMessageHead">
              <strong>{m.sender_name || "Atendente"}</strong>
              <small>{formatTime(m.created_at)}</small>
            </div>
            <p>{m.body}</p>
          </div>
        ))}
      </div>

      <form className="ticketReplyForm" onSubmit={sendMsg}>
        <textarea
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          placeholder="Adicione uma atualização ou comentário…"
          rows={2}
        />
        <button type="submit" disabled={sending || !newMsg.trim()}>
          {sending ? "Enviando…" : "Enviar"}
        </button>
      </form>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function TicketsPage({ api, user }) {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState("resolvido");
  const [bulkNotice, setBulkNotice] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const emptyAdvFilters = { assignees: [], dateFrom: null, dateTo: null, unassignedOnly: false };
  const [advFilters, setAdvFilters] = useState(emptyAdvFilters);
  const [advDraft, setAdvDraft] = useState(emptyAdvFilters);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ticketRes, userRes] = await Promise.all([
        api.get("/tickets"),
        api.get("/users"),
      ]);
      setTickets(ensureArray(ticketRes.data));
      setUsers(ensureArray(userRes.data));
    } catch {}
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function loadSelected(id) {
    try {
      const res = await api.get(`/tickets/${id}`);
      setSelected(ensureObject(res.data));
    } catch {}
  }

  async function createTicket(form) {
    setSaving(true);
    try {
      await api.post("/tickets", form);
      setCreating(false);
      await load();
    } catch {}
    setSaving(false);
  }

  async function updateTicket(form) {
    setSaving(true);
    try {
      await api.put(`/tickets/${editing.id}`, form);
      setEditing(null);
      if (selected?.id === editing.id) await loadSelected(editing.id);
      await load();
    } catch {}
    setSaving(false);
  }

  async function deleteTicket(ticket) {
    if (!window.confirm(`Excluir ticket "${ticket.title}"?`)) return;
    try {
      await api.delete(`/tickets/${ticket.id}`);
      if (selected?.id === ticket.id) setSelected(null);
      await load();
    } catch {}
  }

  const filtered = tickets.filter((t) => {
    if (statusFilter === "open" && (t.status === "resolvido" || t.status === "fechado")) return false;
    if (statusFilter !== "open" && statusFilter !== "all" && t.status !== statusFilter) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (advFilters.unassignedOnly && t.assigned_to) return false;
    if (advFilters.assignees.length && !advFilters.assignees.includes(Number(t.assigned_to))) return false;
    if (advFilters.dateFrom) {
      const d = new Date(t.created_at);
      if (Number.isFinite(d.valueOf()) && d < new Date(advFilters.dateFrom)) return false;
    }
    if (advFilters.dateTo) {
      const d = new Date(t.created_at);
      const end = new Date(advFilters.dateTo);
      end.setHours(23, 59, 59, 999);
      if (Number.isFinite(d.valueOf()) && d > end) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return (t.title || "").toLowerCase().includes(q) || (t.contact_name || "").toLowerCase().includes(q);
    }
    return true;
  });

  const advFilterChips = useMemo(() => {
    const chips = [];
    if (advFilters.unassignedOnly) {
      chips.push({ key: "unassigned", label: "Sem responsável", onRemove: () => setAdvFilters((f) => ({ ...f, unassignedOnly: false })) });
    }
    advFilters.assignees.forEach((id) => {
      const u = users.find((u) => Number(u.id) === Number(id));
      chips.push({
        key: `u:${id}`,
        label: `Resp.: ${u?.name || `#${id}`}`,
        onRemove: () => setAdvFilters((f) => ({ ...f, assignees: f.assignees.filter((x) => x !== id) })),
      });
    });
    if (advFilters.dateFrom) {
      chips.push({ key: "dfrom", label: `De ${advFilters.dateFrom}`, onRemove: () => setAdvFilters((f) => ({ ...f, dateFrom: null })) });
    }
    if (advFilters.dateTo) {
      chips.push({ key: "dto", label: `Até ${advFilters.dateTo}`, onRemove: () => setAdvFilters((f) => ({ ...f, dateTo: null })) });
    }
    return chips;
  }, [advFilters, users]);

  function openAdvFilters() { setAdvDraft(advFilters); setFilterOpen(true); }
  function applyAdvFilters() { setAdvFilters(advDraft); setFilterOpen(false); }
  function resetAdvFilters() { setAdvDraft(emptyAdvFilters); setAdvFilters(emptyAdvFilters); }

  const today = new Date().toDateString();
  const counts = {
    open:          tickets.filter((t) => t.status !== "resolvido" && t.status !== "fechado").length,
    emAtendimento: tickets.filter((t) => t.status === "em_andamento").length,
    aguardando:    tickets.filter((t) => t.status === "aguardando").length,
    resolvidosHoje: tickets.filter((t) => t.status === "resolvido" && new Date(t.updated_at).toDateString() === today).length,
    urgente:       tickets.filter((t) => t.priority === "urgente" && t.status !== "fechado").length,
    all:           tickets.length,
  };

  // ── Bulk selection ─────────────────────────────────────────────────────────
  function toggleSelected(id, e) {
    if (e) e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAllVisible(checked) {
    if (checked) setSelectedIds(new Set(filtered.map((t) => t.id)));
    else setSelectedIds(new Set());
  }
  function clearSelection() { setSelectedIds(new Set()); }

  const allVisibleChecked = useMemo(() => {
    if (filtered.length === 0) return false;
    const allInSet = filtered.every((t) => selectedIds.has(t.id));
    if (allInSet) return true;
    const someInSet = filtered.some((t) => selectedIds.has(t.id));
    return someInSet ? "indeterminate" : false;
  }, [filtered, selectedIds]);

  async function bulkChangeStatus() {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/tickets/bulk-status", {
        ids: Array.from(selectedIds),
        status: bulkStatusValue,
      });
      const { updated = 0 } = res.data || {};
      setBulkStatusOpen(false);
      clearSelection();
      await load();
      setBulkNotice(`${updated} ticket(s) atualizados para "${STATUS_LABELS[bulkStatusValue]}".`);
    } catch (err) {
      setBulkNotice(err.response?.data?.error || "Erro na atualização em lote.");
    } finally { setBulkBusy(false); }
  }

  async function bulkDelete() {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/tickets/bulk-delete", { ids: Array.from(selectedIds) });
      const { deleted = 0 } = res.data || {};
      setBulkConfirmDelete(false);
      if (selected && selectedIds.has(selected.id)) setSelected(null);
      clearSelection();
      await load();
      setBulkNotice(`${deleted} ticket(s) excluído(s).`);
    } catch (err) {
      setBulkNotice(err.response?.data?.error || "Erro na exclusão em lote.");
    } finally { setBulkBusy(false); }
  }

  return (
    <div className="ticketsPage">

      {/* ── Page header ── */}
      <div className="ticketsPageHeader">
        <div>
          <div className="page-title-v2">Tickets</div>
          <div className="page-subtitle-v2">Central de suporte e gestão de solicitações</div>
        </div>
        <div className="page-actions-v2">
          <button type="button" className="btn btn-ghost" onClick={openAdvFilters}>
            <Icon name="filter" size={13} /> Filtros
            {advFilterChips.length > 0 && <em className="filterBadge">{advFilterChips.length}</em>}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={13} /> Novo ticket
          </button>
        </div>
      </div>

      {/* ── KPI strip ── */}
      <div className="ticketsKpiStrip">
        <div className="metric">
          <div className="metric-label">
            <span className="metric-icon" style={{ background: "rgba(59,130,246,.10)", borderColor: "rgba(59,130,246,.25)", color: "#3b82f6" }}><Icon name="ticket" size={11} /></span>
            Abertos
          </div>
          <div className="metric-value">{counts.open}</div>
          <div className="metric-sub">tickets ativos</div>
        </div>
        <div className="metric">
          <div className="metric-label">
            <span className="metric-icon" style={{ background: "rgba(245,158,11,.10)", borderColor: "rgba(245,158,11,.25)", color: "#f59e0b" }}><Icon name="activity" size={11} /></span>
            Em atendimento
          </div>
          <div className="metric-value">{counts.emAtendimento}</div>
          <div className="metric-sub">em andamento</div>
        </div>
        <div className="metric">
          <div className="metric-label">
            <span className="metric-icon" style={{ background: "rgba(167,139,250,.10)", borderColor: "rgba(167,139,250,.25)", color: "#a78bfa" }}><Icon name="clock" size={11} /></span>
            Aguardando
          </div>
          <div className="metric-value">{counts.aguardando}</div>
          <div className="metric-sub">aguardando resposta</div>
        </div>
        <div className="metric">
          <div className="metric-label">
            <span className="metric-icon" style={{ background: "rgba(37,211,102,.10)", borderColor: "rgba(37,211,102,.25)", color: "var(--brand-500)" }}><Icon name="check" size={11} /></span>
            Resolvidos hoje
          </div>
          <div className="metric-value">{counts.resolvidosHoje}</div>
          <div className="metric-sub">encerrados hoje</div>
        </div>
      </div>

      {/* ── Workspace ── */}
      <div className="ticketsWorkspace">
        <div className="ticketsList">
          <div className="tkFiltersPanel">

            {/* Busca com ícone */}
            <div className="tkSearchWrap">
              <Icon name="search" size={13} style={{ color: "var(--text-4)", flexShrink: 0 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar tickets..."
              />
            </div>

            {/* Filtros ativos */}
            {advFilterChips.length > 0 && (
              <ActiveFilters filters={advFilterChips} onClear={() => setAdvFilters(emptyAdvFilters)} />
            )}

            {/* Pills de status */}
            <div className="tkPillsRow">
              {[
                { key: "open",         label: "Abertos",    count: counts.open },
                { key: "em_andamento", label: "Andamento",  count: counts.emAtendimento },
                { key: "aguardando",   label: "Aguardando", count: counts.aguardando },
                { key: "resolvido",    label: "Resolvidos", count: null },
                { key: "all",          label: "Todos",      count: counts.all },
              ].map(({ key, label, count }) => (
                <button key={key} type="button"
                  className={`tkPill${statusFilter === key ? " active" : ""}`}
                  onClick={() => setStatusFilter(key)}
                >
                  {label}
                  {count !== null && <em>{count}</em>}
                </button>
              ))}
            </div>

            {/* Pills de prioridade */}
            <div className="tkPillsRow">
              {[
                { key: "all",     label: "Qualquer", count: null },
                { key: "urgente", label: "Urgente",  count: counts.urgente },
                { key: "alta",    label: "Alta",     count: null },
                { key: "normal",  label: "Normal",   count: null },
                { key: "baixa",   label: "Baixa",    count: null },
              ].map(({ key, label, count }) => (
                <button key={key} type="button"
                  className={`tkPill${priorityFilter === key ? " active" : ""}`}
                  onClick={() => setPriorityFilter(key)}
                >
                  {label}
                  {count !== null && <em>{count}</em>}
                </button>
              ))}
            </div>

          </div>

          {!loading && filtered.length > 0 && (
            <div className="ticketsSelectAll">
              <BulkCheckbox
                checked={allVisibleChecked}
                onChange={toggleAllVisible}
                label={selectedIds.size > 0 ? `${selectedIds.size} selecionado(s)` : "Selecionar todos"}
              />
            </div>
          )}
          {loading ? (
            <SkeletonList rows={6} withAvatar={false} />
          ) : filtered.length === 0 ? (
            <div className="tkEmptyList">
              <div className="tkGhostList tkGhostListLeft">
                {[
                  { id: 1, pillColor: "#ef4444", w1: "w65", w2: "w85", w3: "w40", w4: "w30" },
                  { id: 2, pillColor: "#f59e0b", w1: "w50", w2: "w75", w3: "w35", w4: "w45" },
                  { id: 3, pillColor: "#3b82f6", w1: "w70", w2: "w90", w3: "w50", w4: "w25" },
                ].map((g) => (
                  <div key={g.id} className="tkGhostCard">
                    <div className="tkGhostHead">
                      <div className={`tkGhostBar ${g.w1}`} />
                      <div className="tkGhostPill" style={{ background: g.pillColor }} />
                    </div>
                    <div className={`tkGhostBar ${g.w2}`} style={{ marginBottom: 7 }} />
                    <div className="tkGhostMeta">
                      <div className={`tkGhostBar ${g.w3}`} />
                      <div className={`tkGhostBar ${g.w4}`} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="tkEmptyMsg">
                <p>Nenhum ticket encontrado</p>
                <small>Ajuste os filtros ou crie um novo.</small>
              </div>
            </div>
          ) : (
            filtered.map((t) => (
              <div
                key={t.id}
                className={`ticketItem${selected?.id === t.id ? " selected" : ""}${selectedIds.has(t.id) ? " bulkSelected" : ""} priority-${t.priority}`}
                onClick={() => loadSelected(t.id)}
              >
                <div className="ticketItemHead">
                  <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
                    <BulkCheckbox checked={selectedIds.has(t.id)} onChange={() => toggleSelected(t.id)} />
                  </span>
                  <strong>{t.title}</strong>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <PriorityPill priority={t.priority} />
                    <StatusPillLocal status={t.status} />
                  </div>
                </div>
                <div className="ticketItemMeta">
                  <span>{t.contact_name || "Sem contato"}</span>
                  <span>{t.attendant_name ? `Responsável: ${t.attendant_name}` : "Sem responsável"}</span>
                  <small>{formatTime(t.updated_at || t.created_at)}</small>
                </div>
                <div className="ticketItemActions" onClick={(e) => e.stopPropagation()}>
                  <button className="ghostButton" onClick={() => setEditing(t)}><Icon name="edit" size={11} /></button>
                  <button className="ghostButton danger" onClick={() => deleteTicket(t)}><Icon name="trash" size={11} /></button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="ticketDetail">
          {selected ? (
            <TicketDetail
              ticket={selected}
              users={users}
              api={api}
              onClose={() => setSelected(null)}
              onUpdate={() => { load(); loadSelected(selected.id); }}
            />
          ) : (
            <div className="tkDetailEmpty">
              <div className="tkDetailEmptyHero">
                <div className="tkDetailEmptyIcon">
                  <Icon name="ticket" size={26} />
                </div>
                <div className="tkDetailEmptyText">
                  <h3>Selecione um ticket</h3>
                  <p>Clique em qualquer ticket à esquerda para ver o histórico e responder.</p>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
                  <Icon name="plus" size={13} /> Novo ticket
                </button>
              </div>
              <div className="tkGhostList">
                {GHOST_TICKETS.map((g) => (
                  <div key={g.id} className="tkGhostCard">
                    <div className="tkGhostHead">
                      <div className="tkGhostBar w60" />
                      <div className="tkGhostPill" style={{ background: g.pillColor }} />
                    </div>
                    <div className="tkGhostBar w90" style={{ marginBottom: 7 }} />
                    <div className="tkGhostMeta">
                      <div className="tkGhostBar w40" />
                      <div className="tkGhostBar w30" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {creating && (
        <TicketForm users={users} onSave={createTicket} onClose={() => setCreating(false)} saving={saving} />
      )}
      {editing && (
        <TicketForm ticket={editing} users={users} onSave={updateTicket} onClose={() => setEditing(null)} saving={saving} />
      )}

      {bulkNotice && (
        <div className="sendNotice" style={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", zIndex: 1001 }}>
          {bulkNotice}
          <button className="ghostButton" onClick={() => setBulkNotice("")} style={{ marginLeft: 8 }}>Ok</button>
        </div>
      )}

      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        title="Filtros de tickets"
        onApply={applyAdvFilters}
        onReset={resetAdvFilters}
        activeCount={advFilterChips.length}
      >
        <FilterSection title="Responsável" hint="Selecione um ou mais atendentes (OR)">
          <FilterChipGroup
            options={users.map((u) => ({ value: Number(u.id), label: u.name }))}
            values={advDraft.assignees}
            onChange={(assignees) => setAdvDraft((f) => ({ ...f, assignees }))}
          />
          <label className="uiBulkCheck" style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={advDraft.unassignedOnly}
              onChange={(e) => setAdvDraft((f) => ({ ...f, unassignedOnly: e.target.checked }))}
            />
            <span className="uiBulkCheck-box" aria-hidden="true" />
            <span className="uiBulkCheck-label">Apenas tickets sem responsável</span>
          </label>
        </FilterSection>
        <FilterSection title="Período de criação" hint="Filtra pela data de criação do ticket">
          <FilterDateRange
            from={advDraft.dateFrom}
            to={advDraft.dateTo}
            onChange={({ from, to }) => setAdvDraft((f) => ({ ...f, dateFrom: from, dateTo: to }))}
          />
        </FilterSection>
      </FilterDrawer>

      <BulkActionBar
        count={selectedIds.size}
        total={filtered.length}
        onClear={clearSelection}
        actions={[
          {
            label: "Mudar status",
            variant: "ghost",
            icon: <Icon name="edit" size={12} />,
            onClick: () => setBulkStatusOpen(true),
            disabled: bulkBusy,
          },
          {
            label: "Excluir",
            variant: "danger",
            icon: <Icon name="trash" size={12} />,
            onClick: () => setBulkConfirmDelete(true),
            disabled: bulkBusy,
          },
        ]}
      />

      {bulkConfirmDelete && (
        <div className="modal-overlay-v2">
          <div className="modal-v2 modal-sm">
            <div className="modal-body">
              <div className="confirm-hero confirm-danger">
                <div className="confirm-icon"><Icon name="trash" size={16} /></div>
                <div>
                  <strong style={{ fontSize: "var(--fs-sm)", color: "var(--text-1)", display: "block", marginBottom: 4 }}>
                    Excluir {selectedIds.size} ticket(s)?
                  </strong>
                  <p className="confirm-text">Mensagens vinculadas também serão removidas. Esta ação não pode ser desfeita.</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger btn-sm" onClick={bulkDelete} disabled={bulkBusy}>
                {bulkBusy ? "Excluindo…" : "Excluir todos"}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setBulkConfirmDelete(false)} disabled={bulkBusy}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {bulkStatusOpen && (
        <div className="modal-overlay-v2">
          <div className="modal-v2 modal-sm">
            <div className="modal-header">
              <h3>Mudar status de {selectedIds.size} ticket(s)</h3>
            </div>
            <div className="modal-body">
              <div className="field">
                <label className="field-label-v2">Novo status</label>
                <select value={bulkStatusValue} onChange={(e) => setBulkStatusValue(e.target.value)}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary btn-sm" onClick={bulkChangeStatus} disabled={bulkBusy}>
                {bulkBusy ? "Atualizando…" : "Aplicar"}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setBulkStatusOpen(false)} disabled={bulkBusy}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketsPage;
